import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CASH_RECEIPT_ORIGINS, HOLD_KINDS, PLATFORMS } from '@loot/shared';
import {
  api,
  type BalanceHold,
  type CashAccount,
  type CashReceipt,
  type CashSettingsEntity,
  type LateCashReceipt,
  type PlatformAccount,
  type ReceiptFormOptions,
} from '../../api.ts';
import { changed, FormModal, FormSection } from '../../components/FormModal.tsx';
import { ListToolbar } from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { NetsuiteButton } from '../../components/NetsuiteReferences.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  Panel,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { normalizeDecimal } from '../fpa/expense-form.ts';
import { CommentAction } from '../partners/common.tsx';
import { useFormat } from '../purchasing/common.tsx';
import { CardFilterNotice, useCardFilter } from './card-filter.tsx';
import { CurrencySelect } from '../../components/forms/CurrencySelect.tsx';

const KEY = ['finance', 'treasury'];
const LATE_FILTER = ['late_receipts'] as const;

function useAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.post<Record<string, unknown>>(`/finance/treasury${path}`, body ?? {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

const STATUS_TONE: Record<CashReceipt['status'], 'ok' | 'info' | 'neutral' | 'off'> = {
  expected: 'info',
  received: 'ok',
  cancelled: 'neutral',
  reversed: 'off',
};

/**
 * Recebimentos (ADR 0084, Entrega 5): previstos e recebidos, com bruto, taxas, retenções e líquido;
 * contas de plataforma (Stripe, Paddle...). Só o crédito numa conta bancária é caixa realizado; o
 * saque da plataforma é uma transferência (tela de saldos).
 */
export function TreasuryReceipts() {
  const { t } = useTranslation();
  const [lateOnly, clearLate] = useCardFilter(LATE_FILTER);
  const [entityId, setEntityId] = useState('');
  const settings = useQuery({
    queryKey: [...KEY, 'projection', 'settings'],
    queryFn: () => api.get<{ entities: CashSettingsEntity[] }>('/finance/treasury/settings'),
  });
  const accounts = useQuery({
    queryKey: [...KEY, 'accounts'],
    queryFn: () => api.get<{ accounts: CashAccount[] }>('/finance/treasury/accounts'),
  });
  if (lateOnly) return <LateReceipts onClear={clearLate} />;
  if (settings.isPending || accounts.isPending) return <Loading />;
  if (settings.isError) return <ErrorBox error={settings.error} />;
  if (accounts.isError) return <ErrorBox error={accounts.error} />;
  const entities = settings.data.entities;
  if (entities.length === 0) {
    return (
      <Panel>
        <p className="text-sm text-muted">{t('finance.flow.noEntities')}</p>
      </Panel>
    );
  }
  const entity = entities.find((e) => e.legalEntityId === entityId) ?? entities[0]!;
  const own = accounts.data.accounts.filter((a) => a.legalEntityId === entity.legalEntityId);
  return (
    <div className="flex flex-col gap-6">
      <Receipts entity={entity} entities={entities} accounts={own} onEntity={setEntityId} />
      <Platforms entity={entity} accounts={own} />
    </div>
  );
}

/** Recebimentos atrasados de todas as empresas: a lista do cartão da Visão geral. */
function LateReceipts({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation();
  const action = useAction();
  const [receiving, setReceiving] = useState<CashReceipt | null>(null);
  const data = useQuery({
    queryKey: [...KEY, 'receipts', 'late'],
    queryFn: () => api.get<{ receipts: LateCashReceipt[] }>('/finance/treasury/receipts/late'),
  });
  return (
    <div className="flex flex-col gap-4">
      <CardFilterNotice label={t('finance.homeCards.lateReceipts.title')} onClear={onClear} />
      <ErrorBox error={action.error} />
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {data.isSuccess && (
        <ReceiptsTable
          receipts={data.data.receipts}
          canManage={(r) => r.canManage}
          entityOf={(r) => r.legalEntityCode ?? '—'}
          action={action}
          onReceive={setReceiving}
        />
      )}
      {receiving && <ReceiveForm receipt={receiving} onDone={() => setReceiving(null)} />}
    </div>
  );
}

function ReceiptsTable<R extends CashReceipt>({
  receipts,
  canManage,
  entityOf,
  action,
  onReceive,
}: {
  receipts: R[];
  canManage: (r: R) => boolean;
  /** Lista de várias empresas: mostra de qual empresa é cada recebimento. */
  entityOf?: (r: R) => string;
  action: ReturnType<typeof useAction>;
  onReceive: (r: R) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  return (
    <Table
      head={[
        ...(entityOf ? [t('finance.flow.fields.entity')] : []),
        t('finance.cash.fields.date'),
        t('finance.receipts.fields.description'),
        t('finance.cash.fields.account'),
        { label: t('finance.receipts.fields.gross'), align: 'right' as const },
        { label: t('finance.receipts.fields.deductions'), align: 'right' as const },
        { label: t('finance.receipts.fields.net'), align: 'right' as const },
        t('finance.cash.fields.situation'),
        '',
      ]}
    >
      {receipts.length === 0 && (
        <EmptyRow colSpan={entityOf ? 9 : 8}>{t('finance.receipts.none')}</EmptyRow>
      )}
      {receipts.map((r) => (
        <tr key={r.id}>
          {entityOf && <td className="font-mono text-sm">{entityOf(r)}</td>}
          <td className="whitespace-nowrap">
            {fmt.date(r.effectiveDate ?? r.expectedDate)}
            {r.effectiveDate && r.effectiveDate !== r.expectedDate && (
              <div className="text-xs text-muted">
                {t('finance.receipts.expectedOn', { date: fmt.date(r.expectedDate) })}
              </div>
            )}
          </td>
          <td>
            {r.description}
            <div className="text-xs text-muted">
              {t(`finance.receipts.origins.${r.origin}`)}
              {r.payerName && ` · ${r.payerName}`}
              {r.document && ` · ${r.document}`}
            </div>
          </td>
          <td className="text-sm">
            {r.accountName ?? '—'}
            {r.accountKind === 'platform' && (
              <div className="text-xs text-muted">{t('finance.receipts.platformAccount')}</div>
            )}
          </td>
          <NumCell>{fmt.money(r.grossAmount, r.currency)}</NumCell>
          <NumCell>
            {fmt.money(String(Number(r.feeAmount) + Number(r.withholdingAmount)), r.currency)}
          </NumCell>
          <NumCell>{fmt.money(r.netAmount, r.currency)}</NumCell>
          <td>
            <Badge tone={STATUS_TONE[r.status]}>{t(`finance.receipts.statuses.${r.status}`)}</Badge>
            {r.statusReason && <div className="text-xs text-muted">{r.statusReason}</div>}
          </td>
          <td className="text-right">
            <div className="flex flex-wrap justify-end gap-2">
              {canManage(r) && r.status === 'expected' && (
                <>
                  <Button onClick={() => onReceive(r)}>{t('finance.receipts.receive')}</Button>
                  <CommentAction
                    label={t('finance.receipts.cancel')}
                    fieldLabel={t('finance.cash.fields.reason')}
                    variant="danger"
                    pending={action.isPending}
                    error={action.error}
                    onConfirm={(reason) =>
                      action.mutate({ path: `/receipts/${r.id}/cancel`, body: { reason } })
                    }
                  />
                </>
              )}
              {canManage(r) && r.status === 'received' && (
                <CommentAction
                  label={t('finance.receipts.reverse')}
                  fieldLabel={t('finance.cash.fields.reason')}
                  variant="danger"
                  pending={action.isPending}
                  error={action.error}
                  onConfirm={(reason) =>
                    action.mutate({ path: `/receipts/${r.id}/reverse`, body: { reason } })
                  }
                />
              )}
              <NetsuiteButton entityType="treasury_receipt" entityId={r.id} />
              <MasterDeleteButton table="treasury.receipt" id={r.id} compact />
            </div>
          </td>
        </tr>
      ))}
    </Table>
  );
}

function Receipts({
  entity,
  entities,
  accounts,
  onEntity,
}: {
  entity: CashSettingsEntity;
  entities: CashSettingsEntity[];
  accounts: CashAccount[];
  onEntity: (id: string) => void;
}) {
  const { t } = useTranslation();
  const action = useAction();
  const [form, setForm] = useState<null | { kind: 'new' } | { kind: 'receive'; r: CashReceipt }>(
    null,
  );
  const data = useQuery({
    queryKey: [...KEY, 'receipts', entity.legalEntityId],
    queryFn: () =>
      api.get<{ canManage: boolean; receipts: CashReceipt[] }>(
        `/finance/treasury/receipts?legalEntityId=${entity.legalEntityId}`,
      ),
  });
  const canManage = data.data?.canManage ?? false;
  return (
    <>
      <ListToolbar
        action={
          canManage && (
            <Button onClick={() => setForm({ kind: 'new' })}>{t('finance.receipts.new')}</Button>
          )
        }
      >
        <Select
          id="receipt-entity"
          aria-label={t('finance.flow.fields.entity')}
          value={entity.legalEntityId}
          onChange={(e) => onEntity(e.target.value)}
        >
          {entities.map((e) => (
            <option key={e.legalEntityId} value={e.legalEntityId}>
              {e.code} · {e.name}
            </option>
          ))}
        </Select>
      </ListToolbar>
      <Panel title={t('finance.receipts.title')}>
        <p className="text-sm text-muted">{t('finance.receipts.about')}</p>
        <ErrorBox error={action.error} />
        {data.isPending && <Loading />}
        {data.isError && <ErrorBox error={data.error} />}
        {data.isSuccess && (
          <ReceiptsTable
            receipts={data.data.receipts}
            canManage={() => canManage}
            action={action}
            onReceive={(r) => setForm({ kind: 'receive', r })}
          />
        )}
      </Panel>
      {form?.kind === 'new' && (
        <ReceiptForm entity={entity} accounts={accounts} onDone={() => setForm(null)} />
      )}
      {form?.kind === 'receive' && <ReceiveForm receipt={form.r} onDone={() => setForm(null)} />}
    </>
  );
}

function ReceiptForm({
  entity,
  accounts,
  onDone,
}: {
  entity: CashSettingsEntity;
  accounts: CashAccount[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const save = useAction();
  const options = useQuery({
    queryKey: [...KEY, 'receipt-options', entity.legalEntityId],
    queryFn: () =>
      api.get<ReceiptFormOptions>(
        `/finance/treasury/receipt-form-options?legalEntityId=${entity.legalEntityId}`,
      ),
  });
  const active = accounts.filter((a) => a.accountStatus === 'active');
  const [start] = useState(() => ({
    status: 'expected',
    accountKey: active[0]?.accountKey ?? '',
    expectedDate: '',
    effectiveDate: '',
    grossAmount: '',
    feeAmount: '',
    withholdingAmount: '',
    origin: 'customer',
    payerName: '',
    partnerId: '',
    document: '',
    description: '',
    classificationId: '',
    expenseTypeId: '',
    costCenterId: '',
    notes: '',
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const o = options.data;
  const chosen = o?.classifications.find((c) => c.id === f.classificationId);
  const expenseTypes = (o?.expenseTypes ?? []).filter(
    (e) => !chosen || chosen.expenseTypeIds === null || chosen.expenseTypeIds.includes(e.id),
  );
  const received = f.status === 'received';
  const pick = (
    id: 'classificationId' | 'expenseTypeId' | 'costCenterId' | 'partnerId',
    items: { id: string; label: string }[],
  ) => (
    <Field label={t(`finance.receipts.fields.${id}`)}>
      <Select id={`rc-${id}`} value={f[id]} onChange={set(id)}>
        <option value="">—</option>
        {items.map((i) => (
          <option key={i.id} value={i.id}>
            {i.label}
          </option>
        ))}
      </Select>
    </Field>
  );
  const dec = (v: string) => (v.trim() ? normalizeDecimal(v) : '0');
  return (
    <FormModal
      open
      title={t('finance.receipts.new')}
      description={`${entity.code} · ${entity.name}`}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            path: '/receipts',
            body: {
              legalEntityId: entity.legalEntityId,
              accountKey: f.accountKey,
              status: f.status,
              expectedDate: f.expectedDate || f.effectiveDate,
              effectiveDate: received ? f.effectiveDate : null,
              grossAmount: normalizeDecimal(f.grossAmount),
              feeAmount: dec(f.feeAmount),
              withholdingAmount: dec(f.withholdingAmount),
              origin: f.origin,
              payerName: f.payerName,
              partnerId: f.partnerId || null,
              document: f.document,
              description: f.description,
              classificationId: f.classificationId || null,
              expenseTypeId: f.expenseTypeId || null,
              costCenterId: f.costCenterId || null,
              notes: f.notes,
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.receipts.fields.status')} required>
          <Select id="rc-status" value={f.status} onChange={set('status')}>
            {(['expected', 'received'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`finance.receipts.statuses.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.cash.fields.account')} required>
          <Select id="rc-account" required value={f.accountKey} onChange={set('accountKey')}>
            {active.map((a) => (
              <option key={a.accountKey} value={a.accountKey}>
                {a.nickname} · {a.masked} · {a.currency}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.receipts.fields.expectedDate')} required={!received}>
          <Input
            id="rc-expected"
            type="date"
            required={!received}
            value={f.expectedDate}
            onChange={set('expectedDate')}
          />
        </Field>
        {received && (
          <Field label={t('finance.receipts.fields.effectiveDate')} required>
            <Input
              id="rc-effective"
              type="date"
              required
              value={f.effectiveDate}
              onChange={set('effectiveDate')}
            />
          </Field>
        )}
        <Field label={t('finance.receipts.fields.gross')} required>
          <Input
            id="rc-gross"
            inputMode="decimal"
            required
            value={f.grossAmount}
            onChange={set('grossAmount')}
          />
        </Field>
        <Field label={t('finance.receipts.fields.fees')}>
          <Input id="rc-fees" inputMode="decimal" value={f.feeAmount} onChange={set('feeAmount')} />
        </Field>
        <Field label={t('finance.receipts.fields.withholdings')}>
          <Input
            id="rc-withholdings"
            inputMode="decimal"
            value={f.withholdingAmount}
            onChange={set('withholdingAmount')}
          />
        </Field>
        <Field label={t('finance.receipts.fields.origin')} required>
          <Select id="rc-origin" value={f.origin} onChange={set('origin')}>
            {CASH_RECEIPT_ORIGINS.map((v) => (
              <option key={v} value={v}>
                {t(`finance.receipts.origins.${v}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.receipts.fields.description')} required>
          <Input
            id="rc-description"
            required
            maxLength={300}
            value={f.description}
            onChange={set('description')}
          />
        </Field>
        <Field label={t('finance.receipts.fields.payerName')}>
          <Input id="rc-payer" maxLength={200} value={f.payerName} onChange={set('payerName')} />
        </Field>
        {pick(
          'partnerId',
          (o?.partners ?? []).map((p) => ({ id: p.id, label: p.name })),
        )}
        <Field label={t('finance.receipts.fields.document')}>
          <Input id="rc-document" maxLength={100} value={f.document} onChange={set('document')} />
        </Field>
        {pick(
          'classificationId',
          (o?.classifications ?? []).map((c) => ({ id: c.id, label: c.name })),
        )}
        {pick(
          'expenseTypeId',
          expenseTypes.map((e) => ({ id: e.id, label: e.name })),
        )}
        {pick(
          'costCenterId',
          (o?.costCenters ?? []).map((c) => ({ id: c.id, label: `${c.code} · ${c.name}` })),
        )}
      </FormSection>
      <FormSection columns={1}>
        <Field label={t('finance.receipts.fields.notes')}>
          <Textarea id="rc-notes" maxLength={2000} value={f.notes} onChange={set('notes')} />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function ReceiveForm({ receipt: r, onDone }: { receipt: CashReceipt; onDone: () => void }) {
  const { t } = useTranslation();
  const save = useAction();
  const trim = (v: string) => v.replace(/\.?0+$/, '');
  const [start] = useState(() => ({
    effectiveDate: '',
    grossAmount: trim(r.grossAmount),
    feeAmount: trim(r.feeAmount),
    withholdingAmount: trim(r.withholdingAmount),
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const dec = (v: string) => (v.trim() ? normalizeDecimal(v) : '0');
  return (
    <FormModal
      open
      size="md"
      title={t('finance.receipts.receive')}
      description={r.description}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            path: `/receipts/${r.id}/receive`,
            body: {
              effectiveDate: f.effectiveDate,
              grossAmount: normalizeDecimal(f.grossAmount),
              feeAmount: dec(f.feeAmount),
              withholdingAmount: dec(f.withholdingAmount),
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection columns={1}>
        <Field label={t('finance.receipts.fields.effectiveDate')} required>
          <Input
            id="rr-effective"
            type="date"
            required
            value={f.effectiveDate}
            onChange={set('effectiveDate')}
          />
        </Field>
        <Field label={t('finance.receipts.fields.gross')} required>
          <Input
            id="rr-gross"
            inputMode="decimal"
            required
            value={f.grossAmount}
            onChange={set('grossAmount')}
          />
        </Field>
        <Field label={t('finance.receipts.fields.fees')}>
          <Input id="rr-fees" inputMode="decimal" value={f.feeAmount} onChange={set('feeAmount')} />
        </Field>
        <Field label={t('finance.receipts.fields.withholdings')}>
          <Input
            id="rr-withholdings"
            inputMode="decimal"
            value={f.withholdingAmount}
            onChange={set('withholdingAmount')}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Contas de plataforma da empresa (D-03): cadastro, conta de saque e inativação. */
function Platforms({ entity, accounts }: { entity: CashSettingsEntity; accounts: CashAccount[] }) {
  const { t } = useTranslation();
  const action = useAction();
  const [adding, setAdding] = useState(false);
  const data = useQuery({
    queryKey: [...KEY, 'platform-accounts'],
    queryFn: () =>
      api.get<{ platformAccounts: PlatformAccount[] }>('/finance/treasury/platform-accounts'),
  });
  const list = (data.data?.platformAccounts ?? []).filter(
    (a) => a.legalEntityId === entity.legalEntityId,
  );
  const banks = new Map(accounts.filter((a) => a.kind === 'bank').map((a) => [a.accountKey, a]));
  const canManage = entity.canManage;
  return (
    <Panel title={t('finance.receipts.platforms.title')}>
      <p className="text-sm text-muted">{t('finance.receipts.platforms.about')}</p>
      {canManage && (
        <div>
          <Button variant="secondary" onClick={() => setAdding(true)}>
            {t('finance.receipts.platforms.new')}
          </Button>
        </div>
      )}
      <ErrorBox error={action.error} />
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {data.isSuccess && (
        <Table
          head={[
            t('finance.receipts.platforms.platform'),
            t('finance.receipts.platforms.nickname'),
            t('finance.receipts.platforms.payout'),
            t('finance.cash.fields.situation'),
            '',
          ]}
        >
          {list.length === 0 && (
            <EmptyRow colSpan={5}>{t('finance.receipts.platforms.none')}</EmptyRow>
          )}
          {list.map((a) => (
            <tr key={a.id}>
              <td>{t(`finance.receipts.platforms.names.${a.platform}`)}</td>
              <td>
                {a.nickname} · {a.currency}
              </td>
              <td className="text-sm">
                {a.payoutAccountKey ? (banks.get(a.payoutAccountKey)?.nickname ?? '—') : '—'}
              </td>
              <td>
                <Badge tone={a.status === 'active' ? 'ok' : 'neutral'}>
                  {t(`finance.receipts.platforms.statuses.${a.status}`)}
                </Badge>
                {a.inactivatedReason && (
                  <div className="text-xs text-muted">{a.inactivatedReason}</div>
                )}
              </td>
              <td className="text-right">
                <div className="flex justify-end gap-2">
                  {a.canManage && a.status === 'active' && (
                    <CommentAction
                      label={t('finance.receipts.platforms.inactivate')}
                      fieldLabel={t('finance.cash.fields.reason')}
                      variant="danger"
                      pending={action.isPending}
                      error={action.error}
                      onConfirm={(reason) =>
                        action.mutate({
                          path: `/platform-accounts/${a.id}/inactivate`,
                          body: { reason },
                        })
                      }
                    />
                  )}
                  {a.canManage && a.status === 'inactive' && (
                    <Button
                      variant="secondary"
                      disabled={action.isPending}
                      onClick={() =>
                        action.mutate({ path: `/platform-accounts/${a.id}/reactivate` })
                      }
                    >
                      {t('finance.receipts.platforms.reactivate')}
                    </Button>
                  )}
                  <MasterDeleteButton table="treasury.platform_account" id={a.id} compact />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
      {adding && (
        <PlatformForm entity={entity} banks={[...banks.values()]} onDone={() => setAdding(false)} />
      )}
    </Panel>
  );
}

function PlatformForm({
  entity,
  banks,
  onDone,
}: {
  entity: CashSettingsEntity;
  banks: CashAccount[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const save = useAction();
  const [start] = useState(() => ({
    platform: 'stripe',
    nickname: '',
    currency: entity.functionalCurrency,
    payoutAccountKey: '',
    notes: '',
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const usable = banks.filter((b) => b.currency === f.currency.trim().toUpperCase());
  return (
    <FormModal
      open
      size="md"
      title={t('finance.receipts.platforms.new')}
      description={`${entity.code} · ${entity.name}`}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            path: '/platform-accounts',
            body: {
              legalEntityId: entity.legalEntityId,
              platform: f.platform,
              nickname: f.nickname,
              currency: f.currency.trim().toUpperCase(),
              payoutAccountKey: f.payoutAccountKey || null,
              notes: f.notes,
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection columns={1}>
        <Field label={t('finance.receipts.platforms.platform')} required>
          <Select id="pf-platform" value={f.platform} onChange={set('platform')}>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {t(`finance.receipts.platforms.names.${p}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.receipts.platforms.nickname')} required>
          <Input
            id="pf-nickname"
            required
            maxLength={100}
            value={f.nickname}
            onChange={set('nickname')}
          />
        </Field>
        <Field label={t('finance.forecasts.fields.currency')} required>
          <CurrencySelect
            id="pf-currency"
            value={f.currency}
            onChange={(code) => setF((s) => ({ ...s, currency: code }))}
          />
        </Field>
        <Field
          label={t('finance.receipts.platforms.payout')}
          hint={t('finance.receipts.platforms.payoutHint')}
        >
          <Select id="pf-payout" value={f.payoutAccountKey} onChange={set('payoutAccountKey')}>
            <option value="">—</option>
            {usable.map((b) => (
              <option key={b.accountKey} value={b.accountKey}>
                {b.nickname} · {b.masked}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.receipts.fields.notes')}>
          <Textarea id="pf-notes" maxLength={2000} value={f.notes} onChange={set('notes')} />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Bloqueios e reservas da conta (D-02): saem do saldo disponível enquanto valem. */
export function AccountHolds({ account }: { account: CashAccount }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useAction();
  const [adding, setAdding] = useState(false);
  const data = useQuery({
    queryKey: [...KEY, 'holds', account.accountKey],
    queryFn: () =>
      api.get<{ canManage: boolean; holds: BalanceHold[] }>(
        `/finance/treasury/accounts/${account.accountKey}/holds`,
      ),
  });
  const money = (v: string) => fmt.money(v, account.currency);
  return (
    <Panel title={t('finance.receipts.holds.title')}>
      <p className="text-sm text-muted">{t('finance.receipts.holds.about')}</p>
      {data.data?.canManage && (
        <div>
          <Button variant="secondary" onClick={() => setAdding(true)}>
            {t('finance.receipts.holds.new')}
          </Button>
        </div>
      )}
      <ErrorBox error={action.error} />
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {data.isSuccess && (
        <Table
          head={[
            t('finance.receipts.holds.kind'),
            t('finance.receipts.holds.period'),
            { label: t('finance.cash.fields.amount'), align: 'right' },
            t('finance.cash.fields.situation'),
            '',
          ]}
        >
          {data.data.holds.length === 0 && (
            <EmptyRow colSpan={5}>{t('finance.receipts.holds.none')}</EmptyRow>
          )}
          {data.data.holds.map((h) => (
            <tr key={h.id}>
              <td>
                {t(`finance.receipts.holds.kinds.${h.kind}`)}
                <div className="text-xs text-muted">{h.reason}</div>
              </td>
              <td className="whitespace-nowrap text-sm">
                {fmt.date(h.startDate)} – {h.endDate ? fmt.date(h.endDate) : '…'}
              </td>
              <NumCell>{money(h.amount)}</NumCell>
              <td>
                <Badge tone={h.status === 'active' ? 'off' : 'neutral'}>
                  {t(`finance.receipts.holds.statuses.${h.status}`)}
                </Badge>
                {h.releaseReason && <div className="text-xs text-muted">{h.releaseReason}</div>}
              </td>
              <td className="text-right">
                {data.data.canManage && h.status === 'active' && (
                  <CommentAction
                    label={t('finance.receipts.holds.release')}
                    fieldLabel={t('finance.cash.fields.reason')}
                    pending={action.isPending}
                    error={action.error}
                    onConfirm={(reason) =>
                      action.mutate({ path: `/holds/${h.id}/release`, body: { reason } })
                    }
                  />
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
      {adding && <HoldForm account={account} onDone={() => setAdding(false)} />}
    </Panel>
  );
}

function HoldForm({ account, onDone }: { account: CashAccount; onDone: () => void }) {
  const { t } = useTranslation();
  const save = useAction();
  const [start] = useState(() => ({
    kind: 'judicial',
    amount: '',
    startDate: '',
    endDate: '',
    reason: '',
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <FormModal
      open
      size="md"
      title={t('finance.receipts.holds.new')}
      description={`${account.nickname} · ${account.currency}`}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            path: '/holds',
            body: {
              accountKey: account.accountKey,
              kind: f.kind,
              amount: normalizeDecimal(f.amount),
              startDate: f.startDate,
              endDate: f.endDate || null,
              reason: f.reason,
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection columns={1}>
        <Field label={t('finance.receipts.holds.kind')} required>
          <Select id="hd-kind" value={f.kind} onChange={set('kind')}>
            {HOLD_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`finance.receipts.holds.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.cash.fields.amount')} required>
          <Input
            id="hd-amount"
            inputMode="decimal"
            required
            value={f.amount}
            onChange={set('amount')}
          />
        </Field>
        <Field label={t('finance.receipts.holds.start')} required>
          <Input
            id="hd-start"
            type="date"
            required
            value={f.startDate}
            onChange={set('startDate')}
          />
        </Field>
        <Field label={t('finance.receipts.holds.end')}>
          <Input id="hd-end" type="date" value={f.endDate} onChange={set('endDate')} />
        </Field>
        <Field label={t('finance.cash.fields.reason')} required>
          <Textarea
            id="hd-reason"
            required
            maxLength={1000}
            value={f.reason}
            onChange={set('reason')}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}
